import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  Eye,
  FileText,
  ImagePlus,
  LogOut,
  Plus,
  Package,
  Save,
  Search,
  Trash2,
} from "lucide-react";
import { LOGO_URL } from "../data/config";
import { SPEC_ROWS } from "../data/products";
import {
  loadWebsiteCatalog,
  publishWebsiteCatalog,
  verifyEditorCredential,
  verifyEditorPassword,
} from "../data/productApi";
import { cloneDefaultSiteContent } from "../data/siteContent";
import { SiteContentEditor } from "./SiteContentEditor";

const CREDENTIAL_KEY = "evluxe_editor_session";

const emptySpecs = () =>
  SPEC_ROWS.reduce((result, row) => ({ ...result, [row.key]: "" }), {});

const newProduct = (existingProducts) => {
  let suffix = existingProducts.length + 1;
  let id = `new-model-${suffix}`;
  const ids = new Set(existingProducts.map((product) => product.id));
  while (ids.has(id)) {
    suffix += 1;
    id = `new-model-${suffix}`;
  }
  return {
    id,
    name: "New model",
    series: "GITANJALI",
    tagline: "",
    description: "",
    images: [""],
    colorNames: [""],
    specs: emptySpecs(),
  };
};

const fieldClass =
  "mt-2 w-full rounded-lg border border-white/10 bg-[#11161a] px-3.5 py-3 text-sm text-white outline-none transition focus:border-brand/70 focus:ring-1 focus:ring-brand/50 placeholder:text-white/25";

const Field = ({ label, hint, children }) => (
  <label className="block text-sm text-white/75">
    <span className="font-medium">{label}</span>
    {hint && <span className="ml-2 text-xs font-normal text-white/35">{hint}</span>}
    {children}
  </label>
);

const ProductImagePreview = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className="flex h-full min-h-40 items-center justify-center text-center text-sm text-white/35">
        <div>
          <ImagePlus className="mx-auto mb-2" size={28} />
          {src ? "Image could not be loaded" : "Add an image URL to preview"}
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`${name || "Product"} preview`}
      className="h-full min-h-40 w-full object-contain p-4"
      onError={() => setFailed(true)}
    />
  );
};

export const ProductEditor = () => {
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(() => cloneDefaultSiteContent());
  const [activeArea, setActiveArea] = useState("content");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [password, setPassword] = useState("");
  const [credential, setCredential] = useState(
    () => sessionStorage.getItem(CREDENTIAL_KEY) || ""
  );
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(true);
  const [connected, setConnected] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    document.title = "Website Editor | evLUXE";
    let active = true;

    const initialise = async () => {
      const catalog = await loadWebsiteCatalog();
      if (!active) return;
      setProducts(catalog.products);
      setContent(catalog.content);
      setInitialized(catalog.productsInitialized && catalog.contentInitialized);
      setConnected(catalog.connected);

      const savedCredential = sessionStorage.getItem(CREDENTIAL_KEY);
      if (savedCredential) {
        try {
          await verifyEditorCredential(savedCredential);
          if (active) setAuthenticated(true);
        } catch {
          sessionStorage.removeItem(CREDENTIAL_KEY);
          if (active) setCredential("");
        }
      }
      if (active) setLoading(false);
    };

    initialise();
    return () => {
      active = false;
      document.title = "evLUXE";
    };
  }, []);

  const selected = products[selectedIndex] || null;

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products
      .map((product, index) => ({ product, index }))
      .filter(({ product }) =>
        !query
          ? true
          : `${product.name} ${product.id} ${product.series}`.toLowerCase().includes(query)
      );
  }, [products, search]);

  const signIn = async (event) => {
    event.preventDefault();
    setAuthenticating(true);
    setMessage(null);
    try {
      const session = await verifyEditorPassword(password);
      sessionStorage.setItem(CREDENTIAL_KEY, session.credential);
      setCredential(session.credential);
      setPassword("");
      setAuthenticated(true);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Unable to sign in." });
    } finally {
      setAuthenticating(false);
    }
  };

  const signOut = () => {
    sessionStorage.removeItem(CREDENTIAL_KEY);
    setCredential("");
    setPassword("");
    setAuthenticated(false);
    setMessage(null);
  };

  const updateContent = (nextContent) => {
    setContent(nextContent);
    setDirty(true);
    setMessage(null);
  };

  const updateSelected = (updater) => {
    setProducts((current) =>
      current.map((product, index) =>
        index === selectedIndex ? updater(product) : product
      )
    );
    setDirty(true);
    setMessage(null);
  };

  const setField = (key, value) => {
    updateSelected((product) => ({ ...product, [key]: value }));
  };

  const setSpec = (key, value) => {
    updateSelected((product) => ({
      ...product,
      specs: { ...product.specs, [key]: value },
    }));
  };

  const setImage = (imageIndex, value) => {
    updateSelected((product) => {
      const images = [...product.images];
      images[imageIndex] = value;
      return { ...product, images };
    });
  };

  const setColor = (imageIndex, value) => {
    updateSelected((product) => {
      const colorNames = [...(product.colorNames || [])];
      colorNames[imageIndex] = value;
      return { ...product, colorNames };
    });
  };

  const addImage = () => {
    updateSelected((product) => ({
      ...product,
      images: [...product.images, ""],
      colorNames: [...(product.colorNames || []), ""],
    }));
  };

  const removeImage = (imageIndex) => {
    updateSelected((product) => ({
      ...product,
      images: product.images.filter((_, index) => index !== imageIndex),
      colorNames: (product.colorNames || []).filter((_, index) => index !== imageIndex),
    }));
  };

  const addProduct = () => {
    const product = newProduct(products);
    setProducts((current) => [...current, product]);
    setSelectedIndex(products.length);
    setSearch("");
    setDirty(true);
    setMessage(null);
  };

  const removeProduct = () => {
    if (!selected) return;
    if (!window.confirm(`Remove ${selected.name}? This takes effect when you publish.`)) return;
    setProducts((current) => current.filter((_, index) => index !== selectedIndex));
    setSelectedIndex((current) => Math.max(0, Math.min(current, products.length - 2)));
    setDirty(true);
    setMessage(null);
  };

  const moveProduct = (direction) => {
    const target = selectedIndex + direction;
    if (target < 0 || target >= products.length) return;
    setProducts((current) => {
      const next = [...current];
      [next[selectedIndex], next[target]] = [next[target], next[selectedIndex]];
      return next;
    });
    setSelectedIndex(target);
    setDirty(true);
    setMessage(null);
  };

  const validate = () => {
    const ids = products.map((product) => product.id.trim().toLowerCase());
    if (ids.some((id) => !id)) return "Every product needs an ID.";
    if (new Set(ids).size !== ids.length) return "Product IDs must be unique.";
    for (const product of products) {
      if (!product.name.trim()) return "Every product needs a name.";
      if (!product.images.some((image) => image.trim())) {
        return `${product.name} needs at least one image.`;
      }
    }
    return null;
  };

  const publish = async () => {
    const validationError = validate();
    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }

    setPublishing(true);
    setMessage(null);
    try {
      const cleaned = products.map((product) => {
        const imagePairs = product.images
          .map((image, index) => ({ image: image.trim(), color: product.colorNames?.[index] || "" }))
          .filter(({ image }) => image);
        return {
          ...product,
          id: product.id.trim().toLowerCase(),
          name: product.name.trim(),
          images: imagePairs.map(({ image }) => image),
          colorNames: imagePairs.map(({ color }) => color),
        };
      });
      const catalog = await publishWebsiteCatalog(cleaned, content, credential);
      setProducts(catalog.products);
      setContent(catalog.content);
      setInitialized(true);
      setConnected(true);
      setDirty(false);
      setMessage({
        type: "success",
        text: "Website published. Visitors will see the new text, contact details and products when they refresh.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Publishing failed." });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface-deep text-white">
        <p className="font-mono text-sm text-brand">Loading website editor…</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="relative grid min-h-screen place-items-center overflow-hidden bg-surface-deep px-5 text-white">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden="true" />
        <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1316]/95 p-7 shadow-2xl sm:p-9">
          <a href="/" className="mb-10 inline-flex items-center gap-2 text-sm text-white/55 hover:text-brand">
            <ArrowLeft size={16} /> Back to website
          </a>
          <img src={LOGO_URL} alt="evLUXE" className="mb-7 h-11" />
          <p className="font-mono text-xs font-semibold text-brand">PRIVATE WORKSPACE</p>
          <h1 className="mt-2 font-display text-3xl font-bold">Website editor</h1>
          <p className="mt-3 text-sm leading-relaxed text-white/50">
            Sign in to update website text, contact details, FAQs and products.
          </p>

          <form onSubmit={signIn} className="mt-8">
            <Field label="Editor password">
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={fieldClass}
                autoFocus
                autoComplete="current-password"
                required
              />
            </Field>
            {message?.type === "error" && (
              <p className="mt-3 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {message.text}
              </p>
            )}
            {!connected && (
              <p className="mt-3 text-xs leading-relaxed text-amber-300/80">
                The website data service is temporarily unreachable. Please try again shortly.
              </p>
            )}
            <button
              type="submit"
              disabled={authenticating || !password}
              className="btn-glow mt-6 w-full rounded-full bg-brand px-6 py-3 font-semibold text-black disabled:cursor-not-allowed disabled:opacity-50"
            >
              {authenticating ? "Signing in…" : "Open editor"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-deep text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0a0c]/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center gap-4 px-4 py-3 sm:px-6">
          <img src={LOGO_URL} alt="evLUXE" className="h-8 sm:h-9" />
          <div className="hidden h-7 w-px bg-white/10 sm:block" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-display font-semibold">Website editor</p>
            <p className="hidden text-xs text-white/35 sm:block">
              Text, contacts, FAQs and {products.length} {products.length === 1 ? "model" : "models"} · {dirty || !initialized ? "Unpublished changes" : "Published"}
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-brand/50 hover:text-brand sm:inline-flex"
          >
            <Eye size={15} /> View website
          </a>
          <button
            onClick={signOut}
            className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm text-white/50 hover:text-white"
          >
            <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
          </button>
          <button
            onClick={publish}
            disabled={publishing || (!dirty && initialized)}
            className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-sm font-semibold text-black disabled:cursor-not-allowed disabled:opacity-45 sm:px-5"
          >
            <Save size={16} /> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </header>

      {message && (
        <div className="mx-auto max-w-[1500px] px-4 pt-5 sm:px-6">
          <div
            className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
              message.type === "success"
                ? "border-brand/25 bg-brand/10 text-green-100"
                : "border-red-500/25 bg-red-500/10 text-red-100"
            }`}
          >
            {message.type === "success" && <CheckCircle2 className="mt-0.5 shrink-0 text-brand" size={17} />}
            {message.text}
          </div>
        </div>
      )}

      {!initialized && (
        <div className="mx-auto max-w-[1500px] px-4 pt-5 sm:px-6">
          <div className="rounded-lg border border-amber-400/20 bg-amber-300/10 px-4 py-3 text-sm text-amber-100/85">
            These are the website’s starter contents. Click Publish once to initialise the complete editable website.
          </div>
        </div>
      )}

      <div className="mx-auto flex max-w-[1500px] gap-2 overflow-x-auto px-4 pt-5 sm:px-6">
        <button
          onClick={() => setActiveArea("content")}
          className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm transition ${
            activeArea === "content"
              ? "bg-brand font-semibold text-black"
              : "border border-white/10 text-white/60 hover:border-brand/40 hover:text-white"
          }`}
        >
          <FileText size={16} /> Website content
        </button>
        <button
          onClick={() => setActiveArea("products")}
          className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm transition ${
            activeArea === "products"
              ? "bg-brand font-semibold text-black"
              : "border border-white/10 text-white/60 hover:border-brand/40 hover:text-white"
          }`}
        >
          <Package size={16} /> Products ({products.length})
        </button>
      </div>

      {activeArea === "content" ? (
        <div className="mx-auto max-w-[1200px] px-4 py-5 sm:px-6">
          <SiteContentEditor content={content} onChange={updateContent} />
        </div>
      ) : (
      <div className="mx-auto grid max-w-[1500px] gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="self-start rounded-xl border border-white/10 bg-white/[0.025] lg:sticky lg:top-[86px]">
          <div className="border-b border-white/10 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search models"
                className="w-full rounded-lg border border-white/10 bg-black/20 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand/60"
              />
            </div>
            <button
              onClick={addProduct}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-brand/35 bg-brand/10 py-2.5 text-sm font-semibold text-brand hover:bg-brand/15"
            >
              <Plus size={16} /> Add product
            </button>
          </div>
          <div className="max-h-[45vh] overflow-y-auto p-2 lg:max-h-[calc(100vh-220px)]">
            {filteredProducts.length === 0 ? (
              <p className="p-5 text-center text-sm text-white/35">No matching products.</p>
            ) : (
              filteredProducts.map(({ product, index }) => (
                <button
                  key={`${product.id}-${index}`}
                  onClick={() => setSelectedIndex(index)}
                  className={`mb-1 flex w-full items-center gap-3 rounded-lg p-2.5 text-left transition ${
                    selectedIndex === index ? "bg-brand/15 ring-1 ring-brand/35" : "hover:bg-white/5"
                  }`}
                >
                  <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-white/5">
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt="" className="h-full w-full object-contain" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{product.name || "Unnamed model"}</p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-white/35">{product.id || "no-id"}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <main className="min-w-0">
          {!selected ? (
            <div className="grid min-h-[480px] place-items-center rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-center">
              <div>
                <p className="font-display text-xl font-semibold">No products in the catalogue</p>
                <button onClick={addProduct} className="mt-4 text-sm font-semibold text-brand hover:underline">
                  Add the first product
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <section className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.025]">
                <div className="flex flex-wrap items-center gap-3 border-b border-white/10 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-brand">MODEL {String(selectedIndex + 1).padStart(2, "0")}</p>
                    <h1 className="truncate font-display text-xl font-bold sm:text-2xl">{selected.name}</h1>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveProduct(-1)}
                      disabled={selectedIndex === 0}
                      title="Move up"
                      className="rounded-lg p-2 text-white/55 hover:bg-white/5 hover:text-white disabled:opacity-25"
                    >
                      <ArrowUp size={17} />
                    </button>
                    <button
                      onClick={() => moveProduct(1)}
                      disabled={selectedIndex === products.length - 1}
                      title="Move down"
                      className="rounded-lg p-2 text-white/55 hover:bg-white/5 hover:text-white disabled:opacity-25"
                    >
                      <ArrowDown size={17} />
                    </button>
                    <button
                      onClick={removeProduct}
                      className="ml-2 inline-flex items-center gap-2 rounded-lg border border-red-500/20 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </div>

                <div className="grid gap-5 p-5 sm:grid-cols-2">
                  <Field label="Product name">
                    <input value={selected.name} onChange={(event) => setField("name", event.target.value)} className={fieldClass} />
                  </Field>
                  <Field label="Product ID" hint="lowercase, unique">
                    <input
                      value={selected.id}
                      onChange={(event) => setField("id", event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                      className={fieldClass}
                    />
                  </Field>
                  <Field label="Series">
                    <input value={selected.series} onChange={(event) => setField("series", event.target.value)} className={fieldClass} />
                  </Field>
                  <Field label="Short tagline">
                    <input value={selected.tagline} onChange={(event) => setField("tagline", event.target.value)} className={fieldClass} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Description">
                      <textarea
                        value={selected.description}
                        onChange={(event) => setField("description", event.target.value)}
                        rows={4}
                        className={fieldClass}
                      />
                    </Field>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-white/10 bg-white/[0.025] p-5">
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="font-display text-lg font-semibold">Product images</h2>
                    <p className="mt-1 text-xs text-white/35">Paste a full URL or a site path such as /scooters/model.jpg.</p>
                  </div>
                  <button onClick={addImage} className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-brand hover:underline">
                    <Plus size={15} /> Add image
                  </button>
                </div>
                <div className="space-y-4">
                  {selected.images.map((image, imageIndex) => (
                    <div key={imageIndex} className="grid overflow-hidden rounded-xl border border-white/10 bg-black/15 md:grid-cols-[240px_minmax(0,1fr)]">
                      <div className="image-stage min-h-44 border-0 border-b border-white/10 md:border-b-0 md:border-r">
                        <ProductImagePreview src={image} name={selected.name} />
                      </div>
                      <div className="space-y-4 p-4">
                        <Field label={`Image ${imageIndex + 1} URL or path`}>
                          <input value={image} onChange={(event) => setImage(imageIndex, event.target.value)} className={fieldClass} />
                        </Field>
                        <Field label="Colour name">
                          <input
                            value={selected.colorNames?.[imageIndex] || ""}
                            onChange={(event) => setColor(imageIndex, event.target.value)}
                            className={fieldClass}
                          />
                        </Field>
                        <button onClick={() => removeImage(imageIndex)} className="inline-flex items-center gap-1.5 text-xs text-red-300 hover:underline">
                          <Trash2 size={13} /> Remove image
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-xl border border-white/10 bg-white/[0.025] p-5">
                <h2 className="font-display text-lg font-semibold">Specifications</h2>
                <p className="mt-1 text-xs text-white/35">These values appear on product cards, details and comparisons.</p>
                <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {SPEC_ROWS.map((row) => (
                    <Field key={row.key} label={row.label}>
                      <input
                        value={selected.specs?.[row.key] || ""}
                        onChange={(event) => setSpec(row.key, event.target.value)}
                        className={fieldClass}
                      />
                    </Field>
                  ))}
                </div>
              </section>
            </div>
          )}
        </main>
      </div>
      )}
    </div>
  );
};

export default ProductEditor;
